"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";

import {
  DEFAULT_SORT_VALUE,
  SORT_KEY,
} from "@/components/ui/SearchResultHeader";
import { getRedirectUrl } from "@/data/helper/getRedirectUrl";
import { titleToSlug } from "@/data/helper/titleToSlug";
import { DealType } from "@/enums/dealType";
import { EnumViewMode } from "@/enums/view-mode";
import { usePropertyCategories } from "@/hooks/usePropertyCategories";
import { useUserRole } from "@/hooks/useUserRole";
import { useRouter } from "@/i18n/routing";
import { useViewMode } from "@/providers/ViewContext";
import { propertyService } from "@/services/property.service";
import { RegionResponse, SearchService } from "@/services/search.service";
import { Filter, FilterLocation, RoomsType } from "@/types/SearchFilter.types";
import { StandardProperty } from "@/types/standardProperty.types";
import { initialFilterValue } from "@/utils/initialFilterValue";
import { logger } from "@/utils/logger";
import { parseQueryToFilters } from "@/utils/parseQueryToFilters";
import { serializeFiltersToQuery } from "@/utils/serializeFiltersToQuery";
import { objectFromSearchParams } from "@/utils/urlHelpers";

import { useListingPagination } from "./useListingPagination";
import { getCacheTagByDealType } from "@/utils/getCacheTabByDealType";

// Match server-side PAGE_SIZE constant
const PAGE_SIZE = parseInt(process.env.NEXT_PUBLIC_PAGE_SIZE || "12", 10);

type TInitialFilters = FilterLocation & {
  objectType?: string[];
  propertyType?: string[];
  rooms?: RoomsType[];
};

export interface UseUnifiedListingOptions {
  dealType: DealType;
  basePath: string;
  values?: any; // Server-provided initial data
  // For CatalogPage specific options
  initialFilters?: TInitialFilters;
}

export interface UseUnifiedListingReturn {
  // Data
  data: any;
  transformedData: any[];
  isFetching: boolean;
  isInitializing: boolean; // tracks if filters are being initialized

  // Pagination
  currentPage: number;
  limit: number;
  total: number;
  totalPage: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;

  // UI state
  isEmpty: boolean;
  viewMode: EnumViewMode;
  isRealtor: boolean;

  // Handlers
  refetch: () => void;

  // Filters
  initialFilters: Partial<Filter>;
  regionsListData: RegionResponse | undefined;
  localizedLocationString: string; // Concatenated localized location filters
}

/**
 * Helper function to convert URL location names to localized names
 * Moved from SearchPanel for centralized filter processing
 */
function convertUrlNamesToLocalizedNames(
  urlNames: string[],
  type: keyof FilterLocation,
  regionsListData: RegionResponse | undefined,
  locale: string,
): string[] {
  logger.info(
    `[convertUrlNamesToLocalizedNames] Converting ${type} names:`,
    urlNames,
    `to locale: ${locale}`,
  );

  if (!regionsListData || !urlNames || urlNames.length === 0) {
    logger.warn(
      `[convertUrlNamesToLocalizedNames] Missing data - regionsListData: ${!!regionsListData}, urlNames: ${urlNames}`,
    );
    return [];
  }

  const localizedNames: string[] = [];

  const findLocationItem = (urlName: string, collection: any[]) => {
    return collection.find((item) => {
      return Object.values(item.name).some(
        (name) =>
          typeof name === "string" &&
          name.toLowerCase() === urlName.toLowerCase(),
      );
    });
  };

  const getLocalizedName = (item: any): string => {
    return (
      item.name[locale] ||
      item.name.en ||
      (Object.values(item.name)[0] as string)
    );
  };

  if (type === "region") {
    urlNames.forEach((urlName) => {
      const region = findLocationItem(urlName, regionsListData);
      if (region) {
        const localizedName = getLocalizedName(region);
        logger.info(
          `[convertUrlNamesToLocalizedNames] Region: "${urlName}" → "${localizedName}"`,
        );
        localizedNames.push(localizedName);
      } else {
        logger.warn(
          `[convertUrlNamesToLocalizedNames] Region not found: "${urlName}"`,
        );
        localizedNames.push(urlName);
      }
    });
  } else if (type === "city") {
    urlNames.forEach((urlName) => {
      let found = false;
      for (const region of regionsListData) {
        if (found || !region.cities) break;
        const city = findLocationItem(urlName, region.cities);
        if (city) {
          localizedNames.push(getLocalizedName(city));
          found = true;
        }
      }
      if (!found) {
        localizedNames.push(urlName);
      }
    });
  } else if (type === "district") {
    urlNames.forEach((urlName) => {
      let found = false;
      for (const region of regionsListData) {
        if (found) break;
        for (const city of region.cities || []) {
          if (found || !city.districts) break;
          const district = findLocationItem(urlName, city.districts);
          if (district) {
            localizedNames.push(getLocalizedName(district));
            found = true;
            break;
          }
        }
      }
      if (!found) {
        localizedNames.push(urlName);
      }
    });
  } else if (type === "microdistrict") {
    urlNames.forEach((urlName) => {
      let found = false;
      for (const region of regionsListData) {
        if (found) break;
        for (const city of region.cities || []) {
          if (found) break;
          for (const district of city.districts || []) {
            if (found || !district.microdistricts) break;
            const micro = findLocationItem(urlName, district.microdistricts);
            if (micro) {
              localizedNames.push(getLocalizedName(micro));
              found = true;
              break;
            }
          }
        }
      }
      if (!found) {
        localizedNames.push(urlName);
      }
    });
  } else if (type === "street") {
    urlNames.forEach((urlName) => {
      let found = false;
      for (const region of regionsListData) {
        if (found) break;
        for (const city of region.cities || []) {
          if (found) break;
          for (const district of city.districts || []) {
            if (found || !district.streets) break;
            const street = findLocationItem(urlName, district.streets);
            if (street) {
              localizedNames.push(getLocalizedName(street));
              found = true;
              break;
            }
          }
        }
      }
      if (!found) {
        localizedNames.push(urlName);
      }
    });
  } else if (type === "metroStation") {
    urlNames.forEach((urlName) => {
      let found = false;
      for (const region of regionsListData) {
        if (found || !region.cities) break;
        for (const city of region.cities) {
          if (found || !city.metroStations) break;
          const metroStation = findLocationItem(urlName, city.metroStations);
          if (metroStation) {
            localizedNames.push(getLocalizedName(metroStation));
            found = true;
            break;
          }
        }
      }
      if (!found) {
        localizedNames.push(urlName);
      }
    });
  }

  logger.info(
    `[convertUrlNamesToLocalizedNames] Final result for ${type}:`,
    localizedNames,
  );
  return localizedNames;
}

/**
 * Unified listing hook for Buy/Developers/Rent/Catalog pages
 * Handles data fetching, transformation, and pagination consistently
 *
 * Features:
 * - Unified useQuery approach (no useInfiniteQuery)
 * - Standardized data access via flattenPropertyData
 * - Links-based pagination only
 * - Table and card view support
 * - Server data with minimal client transformation
 * - Filter initialization and localization (moved from SearchPanel)
 */
export function useUnifiedListing({
  dealType,
  basePath,
  values,
  initialFilters,
}: UseUnifiedListingOptions): UseUnifiedListingReturn {
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isRealtor } = useUserRole();
  const { viewMode } = useViewMode();
  const { propertyTypes } = usePropertyCategories();

  const filteredPropertyType = useMemo(() => {
    if (
      propertyTypes &&
      initialFilters?.objectType?.length &&
      !initialFilters?.propertyType?.length
    ) {
      const targetPropertyType = propertyTypes.find(
        (cat) =>
          cat.category.startsWith(dealType) &&
          initialFilters?.objectType?.[0] &&
          cat.subcategories.includes(initialFilters?.objectType?.[0]),
      );
      if (targetPropertyType) {
        return targetPropertyType.category;
      }
    }
    return null;
  }, [propertyTypes, initialFilters, dealType]);

  if (filteredPropertyType && initialFilters) {
    initialFilters.propertyType = [filteredPropertyType];
  }

  // Track if we're replacing the URL with localized names
  const [isReplacingUrl, setIsReplacingUrl] = useState(false);

  // Track the last URL that was processed to avoid re-processing
  // const lastProcessedUrlRef = useRef<string>("");

  // Fetch regions data for filter localization
  const { data: regionsListData, isLoading: isLoadingRegions } = useQuery({
    queryKey: ["getRegions"],
    queryFn: () => SearchService.getRegions(),
    staleTime: Infinity, // Regions rarely change
  });

  // Build location options from regions data
  const locationOptions = useMemo<{
    items: any[];
    regions: string[];
  }>(() => {
    if (!regionsListData) {
      return { items: [], regions: [] };
    }

    const regions: string[] = [];
    regionsListData.forEach((region) => {
      regions.push(region.name[locale]);
    });

    return { items: [], regions };
  }, [regionsListData, locale]);

  // Use unified pagination hook
  const { currentPage, limit, setPage, setPageSize } = useListingPagination({
    basePath,
    defaultLimit: PAGE_SIZE,
  });

  // Simplified search params detection
  const hasSearchParams = useMemo(() => {
    return searchParams?.toString().length > 0;
  }, [searchParams]);

  // Check if we have filters that require SearchService (location + objectType)
  const hasLocationFilters = useMemo(() => {
    return !!(
      initialFilters?.objectType?.length ||
      initialFilters?.city?.length ||
      initialFilters?.district?.length ||
      initialFilters?.microdistrict?.length ||
      initialFilters?.street?.length ||
      initialFilters?.metroStation?.length
    );
  }, [initialFilters]);

  const filterSignature = useMemo(() => {
    const sp = new URLSearchParams(searchParams?.toString() || "");
    sp.delete("page");
    return sp.toString();
  }, [searchParams]);

  const prevFilterSignatureRef = useRef(filterSignature);

  // Get region by city (English value)
  const findRegionByCity = (
    regionsListData?: RegionResponse,
    city?: string,
    locale: string,
  ): string | null => {
    if (!city || !regionsListData) return null;

    const region = regionsListData.find((region) =>
      region.cities.some(
        (c) => c.name["en"].toLowerCase() === city.toLowerCase(),
      ),
    );

    return region?.name[locale] ?? null;
  };

  // Convert all location types without repeating logic
  const LOCATION_TYPES = [
    "region",
    "city",
    "district",
    "microdistrict",
    "street",
    "metroStation",
  ] as const;

  const convertLocationFilters = (
    parsed: TInitialFilters,
    regionsListData: RegionResponse,
    locale: string,
    skipTypes?: string[],
  ) => {
    const result: TInitialFilters = { ...parsed };
    const localizedStrings: string[] = [];

    for (const type of LOCATION_TYPES) {
      const values = parsed[type];
      if (!values?.length || skipTypes?.includes(type)) continue;

      const computed = convertUrlNamesToLocalizedNames(
        values,
        type,
        regionsListData,
        locale,
      );

      result[type] = values;
      localizedStrings.push(...computed);
    }

    return {
      result,
      localizedString: localizedStrings.filter(Boolean).join(", "),
    };
  };

  // Decide final fallback initial filters
  const resolveInitialFallback = (
    initialFilters: TInitialFilters | undefined,
    dealType: DealType,
  ) => ({
    computedInitialFilters: {
      ...initialFilterValue(dealType),
      ...(initialFilters ?? {}),
    },
    localizedLocationString: "",
  });

  // Initialize filters with URL params and localization logic
  // Moved from SearchPanel to centralize filter logic
  const { computedInitialFilters, localizedLocationString } = useMemo<{
    computedInitialFilters: TInitialFilters;
    localizedLocationString: string;
  }>(() => {
    try {
      if (!regionsListData) {
        return resolveInitialFallback(initialFilters, dealType);
      }

      const regionParam =
        searchParams?.get("region") ?? searchParams?.get("region[]") ?? null;

      const cityParam =
        searchParams?.get("city") ??
        searchParams?.get("city[]") ??
        initialFilters?.city?.[0];

      // Try resolving region by city when URL does not contain region
      const targetRegion = !regionParam
        ? findRegionByCity(regionsListData, cityParam, "en")
        : null;

      // Case 1: Parse URL params
      if ((hasSearchParams && regionParam) || targetRegion) {
        logger.info("Parsing filters from URL…");

        // If region was inferred, inject into initialFilters (non-mutating)
        const parsedFilters = targetRegion
          ? { ...initialFilters, region: [targetRegion] }
          : parseQueryToFilters(
              searchParams as URLSearchParams,
              dealType,
              locationOptions.regions,
            );

        const { result, localizedString } = convertLocationFilters(
          parsedFilters,
          regionsListData,
          locale,
          targetRegion ? ["region"] : undefined, // on Catalog page don't show region in search input
        );

        logger.info("Converted parsed filter values:", result);

        return {
          computedInitialFilters: result,
          localizedLocationString: localizedString,
        };
      }

      // Case 2: Use provided initial filters
      if (initialFilters && Object.keys(initialFilters).length > 0) {
        logger.info("Using initialFilters");
        return resolveInitialFallback(initialFilters, dealType);
      }

      // Case 3: Default filters
      logger.info("Using default filters");
      return resolveInitialFallback(undefined, dealType);
    } catch (error) {
      console.error("Error computing initial filters:", error);
      return resolveInitialFallback(undefined, dealType);
    }
  }, [
    searchParams,
    hasSearchParams,
    regionsListData,
    dealType,
    locationOptions.regions,
    initialFilters,
    locale,
  ]);

  // Unified useQuery - works for all 4 page types
  const { data, isFetching, refetch } = useQuery({
    queryKey:
      hasSearchParams || hasLocationFilters
        ? [
            `search${dealType}`,
            searchParams?.toString(),
            JSON.stringify(initialFilters),
            currentPage,
          ]
        : [`default${dealType}`, currentPage],
    queryFn: () => {
      // Use SearchService when we have searchParams OR location filters
      if (hasSearchParams || hasLocationFilters) {
        // Start with existing searchParams or create new URLSearchParams
        const sp = new URLSearchParams(searchParams?.toString() || "");

        // Set pagination and dealType
        sp.set("limit", String(limit));
        sp.set("page", String(currentPage));

        // Ensure dealType is set for search
        if (!sp.get("dealType")) {
          sp.set("dealType", dealType);
        }

        const isGoldenVisaEligible = searchParams.get("isGoldenVisaEligible");

        console.log(
          "IS GOLDEN",
          typeof isGoldenVisaEligible,
          isGoldenVisaEligible,
        );

        if (isGoldenVisaEligible) {
          sp.set("isGoldenVisaEligible", true);
        }

        // Apply initial filters if provided (CatalogPage with location filters)
        if (initialFilters && hasLocationFilters) {
          // Add location filters to query params using array notation for backend DTO validation
          if (initialFilters.objectType?.length) {
            initialFilters.objectType.forEach((val) =>
              sp.append("objectType[]", val),
            );
          }
          if (initialFilters.city?.length) {
            initialFilters.city.forEach((val) => sp.append("city[]", val));
          }
          if (initialFilters.district?.length) {
            initialFilters.district.forEach((val) =>
              sp.append("district[]", val),
            );
          }
          if (initialFilters.microdistrict?.length) {
            initialFilters.microdistrict.forEach((val) =>
              sp.append("microdistrict[]", val),
            );
          }
          if (initialFilters.street?.length) {
            initialFilters.street.forEach((val) => sp.append("street[]", val));
          }
          if (initialFilters.rooms?.length) {
            initialFilters.rooms.forEach((val) =>
              sp.append("rooms[]", String(val)),
            );
          }
        }

        console.log("SP", sp.toString());

        return SearchService.getAllProperty(
          sp as any,
          '',
          getCacheTagByDealType(dealType),
        );
      } else {
        // Default fetch without location filters - use propertyService
        const params: any = {
          category: dealType,
          page: currentPage,
          limit,
        };

        // Apply non-location filters if provided
        if (initialFilters) {
          if (initialFilters.objectType?.length) {
            params.objectType = initialFilters.objectType;
          }
          if (initialFilters.rooms?.length) {
            params.rooms = initialFilters.rooms;
          }
        }

        return propertyService.fetchAllProperty(params, getCacheTagByDealType(dealType));
      }
    },
    // Use server data only for page 1 without search params AND without location filters
    initialData:
      !hasSearchParams && !hasLocationFilters && currentPage === 1
        ? values
        : undefined,
    placeholderData:
      !hasSearchParams && !hasLocationFilters && currentPage === 1
        ? values
        : undefined,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0
  });

  // Simplified pagination calculations - MUST BE BEFORE useEffect that uses them
  const total = useMemo(() => data?.total || 0, [data]);
  const totalPage = useMemo(() => {
    const serverTotalPage = data?.totalPage;
    const calculatedTotalPage = Math.ceil(total / (data?.limit || limit));
    return serverTotalPage || calculatedTotalPage;
  }, [data, total, limit]);

  // Auto-refetch on page/search changes or location filter changes
  useEffect(() => {
    if (hasSearchParams || hasLocationFilters || currentPage > 1) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentPage, pathname, hasLocationFilters]);

  useEffect(() => {
    if (currentPage > 1 && totalPage > 0 && currentPage > totalPage) {
      setPage(totalPage || 1);
    }
  }, [currentPage, totalPage, setPage]);

  useEffect(() => {
    if (prevFilterSignatureRef.current !== filterSignature) {
      prevFilterSignatureRef.current = filterSignature;
      if (currentPage !== 1) {
        setPage(1);
      }
    }
  }, [filterSignature, currentPage, setPage]);

  // Unified data transformation - consistent across all pages
  const transformedData = useMemo(() => {
    if (!data?.data) return [];

    // For table view, use PropertyService transformation
    if (viewMode === EnumViewMode.TABLE && isRealtor) {
      const PropertyService = propertyService.constructor as any;
      return PropertyService.transformTableData(data, { locale });
    }

    // For card/list view: unified transformation across all deal types
    return data.data.map((item: StandardProperty) => {
      return {
        ...item,
        // Ensure required fields with proper fallbacks
        id: item.id || item._id || item.customId || "N/A",
        slug: item.slug || titleToSlug(item.mainInfo?.title || "n-a"),
        location: item.location || item.city || "N/A",
        redirectLink:
          item.redirectLink || getRedirectUrl(item.dealType || dealType),
      };
    });
  }, [data, locale, viewMode, isRealtor, dealType]);

  const isEmpty = !transformedData?.length;

  // Compute comprehensive initialization state for stitchless rendering
  // Show loader when:
  // 1. Regions data is loading (needed for filter localization)
  // 2. URL needs localization but hasn't been replaced yet
  // 3. URL is being replaced with localized names
  // 4. Initial data fetch with filters is in progress
  // 5. Data not yet available
  const isInitializing = useMemo(() => {
    // Still loading regions data - critical for localization
    if (isLoadingRegions) {
      logger.info("useUnifiedListing: Initializing - loading regions data");
      return true;
    }

    // Waiting for initial data fetch when we have search params with regions
    const region = searchParams?.get("region") ?? searchParams?.get("region[]");
    if (hasSearchParams && region && !data) {
      logger.info(
        "useUnifiedListing: Initializing - no data yet with region filters",
      );
      return true;
    }

    // Data is being fetched for the first time
    if (!data && isFetching) {
      logger.info("useUnifiedListing: Initializing - fetching initial data");
      return true;
    }

    const savedSort = localStorage.getItem(SORT_KEY);
    const currentParams = objectFromSearchParams(searchParams);

    const finalSort =
      savedSort ?? (currentParams.sort as string) ?? DEFAULT_SORT_VALUE;

    if (!savedSort) {
      localStorage.setItem(SORT_KEY, finalSort);
    }

    if (!currentParams.sort && finalSort !== DEFAULT_SORT_VALUE) {
      router.replace(
        {
          pathname,
          query: serializeFiltersToQuery({
            ...currentParams,
            sort: finalSort,
          }),
        },
        { scroll: false },
      );

      return true;
    }

    return false;
  }, [
    isLoadingRegions,
    searchParams,
    hasSearchParams,
    data,
    isFetching,
    router,
    pathname,
  ]);

  return {
    // Data
    data,
    transformedData,
    isFetching,
    isInitializing,

    // Pagination
    currentPage,
    limit,
    total,
    totalPage,
    setPage,
    setPageSize,

    // UI state
    isEmpty,
    viewMode,
    isRealtor,

    // Handlers
    refetch,

    // Filters
    initialFilters: computedInitialFilters,
    regionsListData,
    localizedLocationString,
  };
}
